/**
 * Thin WebAuthn wrapper for local app-unlock (device-bound passkeys).
 *
 * We use this as a client-only gate over an already-authenticated session
 * cookie. The backend has zero knowledge of these credentials — the only
 * thing we persist is the credential ID, which we feed into `allowCredentials`
 * on each unlock. The OS-level biometric prompt IS the verification.
 */

function b64encode(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function b64decode(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/** True if the platform authenticator (Face ID, Touch ID, Windows Hello, etc.) is available. */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

interface RegisterUser {
  id: string;
  login: string;
  displayName: string;
}

/**
 * Create a platform-bound passkey for the given user.
 * Returns the credential ID (base64) which the caller should persist.
 */
export async function registerPasskey(user: RegisterUser): Promise<string> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const created = (await navigator.credentials.create({
    publicKey: {
      challenge,
      rp: { name: 'Hoopla POS', id: location.hostname },
      user: {
        id: new TextEncoder().encode(user.id),
        name: user.login,
        displayName: user.displayName,
      },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' }, // ES256
        { alg: -257, type: 'public-key' }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'preferred',
      },
      timeout: 60_000,
      attestation: 'none',
    },
  })) as PublicKeyCredential | null;

  if (!created) throw new Error('registration cancelled');
  return b64encode(created.rawId);
}

/**
 * Trigger a biometric prompt for the previously-registered credential.
 * Returns true if the user authenticated, false on cancel/timeout/failure.
 */
export async function verifyPasskey(credentialId: string): Promise<boolean> {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) return false;
  try {
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const result = await navigator.credentials.get({
      publicKey: {
        challenge,
        rpId: location.hostname,
        allowCredentials: [
          {
            id: b64decode(credentialId).buffer as ArrayBuffer,
            type: 'public-key',
          },
        ],
        userVerification: 'required',
        timeout: 60_000,
      },
    });
    return result != null;
  } catch {
    return false;
  }
}
